import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { CtaSource } from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import {
  convertFssEntityScopeToChatSource,
  isFSSEntityWithFileId,
} from "@mirage/service-conversation/utils/convertFssEntityToChatSource";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useConversationEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import {
  ConversationSource,
  type ConversationEvent,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { tagged } from "@mirage/service-logging";
import { useMountEffect } from "@react-hookz/web";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const logger = tagged("[fss] useConversationEventOrchestrator");

const processedEvents = new Set<string>();

/**
 * This hook is responsible for orchestrating the conversation events. It listens to events from the
 * FSS bridge and builds the appropriate router state to navigate to the appropriate conversation.
 *
 * Each received message is only processed once, ensured by the unique uuid. This is necessary due to
 * the potential for multiple renders on the parent component triggering multiple calls to this hook.
 */
export const useConversationEventOrchestrator = ({
  onRequestNewSession,
}: {
  onRequestNewSession: (routerState?: ConversationRouterState) => void;
}) => {
  const event = useConversationEvents();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isFolderScopedChatEnabled = useFeatureFlagValueBool(
    "bridge_fss_chat_2025_07_28_folder_scoped_chat",
  );

  useMountEffect(() => {
    logger.info("initializing conversation event listener");
  });

  useEffect(() => {
    logger.debug("Attempting to process event: ", event);

    if (!event || processedEvents.has(event.uuid)) {
      return;
    }

    logger.info("Processing event uuid: ", event.uuid);

    processedEvents.add(event.uuid);

    let routerState: ConversationRouterState | undefined;
    switch (event.source) {
      case ConversationSource.FssUnscoped: {
        routerState = buildConversationRouterState(CtaSource.FssUnscoped, {
          surface: event.surface,
        });
        break;
      }
      case ConversationSource.FssSerpQuery: {
        routerState = buildConversationRouterState(CtaSource.FssAskQuery, {
          message: event.data.searchQuery,
          scenarioMeta: {
            type: "fss_serp_query",
            searchSessionId: event.data.searchSessionId,
            query: event.data.searchQuery,
          },
          surface: event.surface,
        });
        break;
      }
      case ConversationSource.FssSerpTypeahead: {
        routerState = buildConversationRouterState(
          CtaSource.FssAskFromSuggestion,
          {
            message: event.data.searchQuery,
            agentIds: ["conversations"],
            attachments: event.data.docIds ?? [],
            scenarioMeta: {
              type: "fss_serp_query",
              searchSessionId: event.data.searchSessionId,
              query: event.data.searchQuery,
            },
            surface: event.surface,
          },
        );
        break;
      }
      case ConversationSource.FssFilePreview: {
        routerState = buildConversationRouterState(CtaSource.FssAskWithFile, {
          scenarioMeta: {
            type: "fss_file_preview",
            file: event.data.file,
          },
          surface: event.surface,
        });
        break;
      }
      case ConversationSource.FssFile: {
        if (event.agentIds?.includes("summarize")) {
          routerState = buildConversationRouterState(
            CtaSource.FssFileSummarize,
            {
              chatSources: event.data.files.map((file) =>
                convertFssEntityScopeToChatSource(file, "summary"),
              ),
              scenarioMeta: {
                type: "other",
              },
              surface: event.surface,
            },
          );
        } else {
          routerState = buildConversationRouterState(
            CtaSource.FssAskWithBrowseFile,
            {
              chatSources: event.data.files.map((file) =>
                convertFssEntityScopeToChatSource(file, "manual"),
              ),
              surface: event.surface,
            },
          );
        }
        break;
      }
      case ConversationSource.FssFolder:
        if (event.agentIds?.includes("summarize")) {
          routerState = buildConversationRouterState(
            CtaSource.FssFolderSummarize,
            {
              chatSources: event.data.folders
                .filter(isFSSEntityWithFileId)
                .map((folder) =>
                  convertFssEntityScopeToChatSource(folder, "summary"),
                ),
              scenarioMeta: {
                type: "other",
              },
              surface: event.surface,
            },
          );
        } else {
          if (isFolderScopedChatEnabled) {
            routerState = buildConversationRouterState(
              CtaSource.FssAskWithFolder,
              {
                scenarioMeta: {
                  type: "fss_folder",
                  folder: event.data.folders[0],
                },
                surface: event.surface,
              },
            );
          } else {
            // else, treat it as an unscoped message
            routerState = buildConversationRouterState(CtaSource.FssUnscoped, {
              surface: event.surface,
            });
            break;
          }
        }
        break;
      case ConversationSource.FssStack:
        routerState = buildConversationRouterState(CtaSource.FssAskWithStack, {
          scenarioMeta: {
            files: event.data.files,
            type: "fss_stack",
          },
          surface: event.surface,
        });
        break;
      default:
        event satisfies never;
        throw new Error(
          `Unexpected source type: ${(event as ConversationEvent).source}`,
        );
    }
    if (routerState) {
      if (event.data.createNewSession) {
        onRequestNewSession(routerState);
        return;
      }
      navigate(pathname, {
        state: routerState,
      });
    }
  }, [
    event,
    navigate,
    onRequestNewSession,
    pathname,
    isFolderScopedChatEnabled,
  ]);
};
